import { useState } from "react";
import { Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle, DialogFooter } from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { PiktableLoader } from "@/components/PiktableLoader";
import {
  useAdminFeedbacks,
  getStatusLabel,
  getTypeLabel,
  getPriorityLabel,
  getStatusColor,
  getTypeColor,
  getPriorityColor,
  Feedback,
  FeedbackType,
  FeedbackStatus,
  FeedbackPriority,
} from "@/hooks/useAdminFeedbacks";
import { supabase } from "@/integrations/supabase/client";
import {
  ArrowLeft,
  Bug,
  Lightbulb,
  HelpCircle,
  MessageSquare,
  Search,
  ExternalLink,
  Mail,
  Building2,
  Globe,
  Monitor,
  Calendar,
  Trash2,
  Image as ImageIcon,
} from "lucide-react";
import { format } from "date-fns";
import { fr } from "date-fns/locale";
import { cn } from "@/lib/utils";

const typeIcons: Record<FeedbackType, typeof Bug> = {
  bug: Bug,
  suggestion: Lightbulb,
  question: HelpCircle,
  other: MessageSquare,
};

export default function AdminFeedbacks() {
  const { feedbacks, stats, loading, filters, setFilters, updateFeedbackStatus, updateFeedbackPriority, updateFeedbackNotes, deleteFeedback } = useAdminFeedbacks();
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [adminNotes, setAdminNotes] = useState('');
  const [deleteConfirmId, setDeleteConfirmId] = useState<string | null>(null);

  const handleOpenDetail = (feedback: Feedback) => {
    setSelectedFeedback(feedback);
    setAdminNotes(feedback.admin_notes || '');
  };

  const handleSaveNotes = async () => {
    if (selectedFeedback) {
      await updateFeedbackNotes(selectedFeedback.id, adminNotes);
      setSelectedFeedback({ ...selectedFeedback, admin_notes: adminNotes });
    }
  };

  const handleStatusChange = async (status: FeedbackStatus) => {
    if (selectedFeedback) {
      await updateFeedbackStatus(selectedFeedback.id, status);
      setSelectedFeedback({ ...selectedFeedback, status });
    }
  };

  const handlePriorityChange = async (priority: FeedbackPriority) => {
    if (selectedFeedback) {
      await updateFeedbackPriority(selectedFeedback.id, priority);
      setSelectedFeedback({ ...selectedFeedback, priority });
    }
  };

  const getScreenshotUrl = (path: string) => {
    const { data } = supabase.storage.from('feedback-screenshots').getPublicUrl(path);
    return data.publicUrl;
  };

  if (loading) {
    return <PiktableLoader message="Chargement des feedbacks..." />;
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-background via-muted/20 to-background">
      <div className="container mx-auto p-6 max-w-7xl space-y-6">
        {/* Header */}
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <Link to="/admin">
              <Button variant="ghost" size="icon">
                <ArrowLeft className="h-5 w-5" />
              </Button>
            </Link>
            <div>
              <h1 className="text-2xl font-serif font-bold flex items-center gap-3">
                <MessageSquare className="h-7 w-7 text-teal-500" />
                Feedbacks utilisateurs
              </h1>
              <p className="text-muted-foreground">
                Gérez les retours des utilisateurs
              </p>
            </div>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-6 gap-4">
          <Card className="bg-gradient-to-br from-teal-500/10 to-teal-600/5 border-teal-500/20">
            <CardHeader className="pb-2">
              <CardDescription>Total</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-teal-600">{stats?.total || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-blue-500/10 to-blue-600/5 border-blue-500/20">
            <CardHeader className="pb-2">
              <CardDescription>Cette semaine</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-blue-600">{stats?.newThisWeek || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-red-500/10 to-red-600/5 border-red-500/20">
            <CardHeader className="pb-2">
              <CardDescription>Bugs</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-red-600">{stats?.byType.bug || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-amber-500/10 to-amber-600/5 border-amber-500/20">
            <CardHeader className="pb-2">
              <CardDescription>Suggestions</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-amber-600">{stats?.byType.suggestion || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-purple-500/10 to-purple-600/5 border-purple-500/20">
            <CardHeader className="pb-2">
              <CardDescription>Nouveaux</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-purple-600">{stats?.byStatus.new || 0}</p>
            </CardContent>
          </Card>
          <Card className="bg-gradient-to-br from-green-500/10 to-green-600/5 border-green-500/20">
            <CardHeader className="pb-2">
              <CardDescription>Résolus</CardDescription>
            </CardHeader>
            <CardContent>
              <p className="text-2xl font-bold text-green-600">{stats?.byStatus.resolved || 0}</p>
            </CardContent>
          </Card>
        </div>

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4">
              <div className="flex-1 min-w-[200px]">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder="Rechercher..."
                    value={filters.search || ''}
                    onChange={(e) => setFilters({ ...filters, search: e.target.value })}
                    className="pl-9"
                  />
                </div>
              </div>
              <Select
                value={filters.type || 'all'}
                onValueChange={(value) => setFilters({ ...filters, type: value as FeedbackType | 'all' })}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Type" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les types</SelectItem>
                  <SelectItem value="bug">Bug</SelectItem>
                  <SelectItem value="suggestion">Suggestion</SelectItem>
                  <SelectItem value="question">Question</SelectItem>
                  <SelectItem value="other">Autre</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.status || 'all'}
                onValueChange={(value) => setFilters({ ...filters, status: value as FeedbackStatus | 'all' })}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Statut" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Tous les statuts</SelectItem>
                  <SelectItem value="new">Nouveau</SelectItem>
                  <SelectItem value="in_progress">En cours</SelectItem>
                  <SelectItem value="resolved">Résolu</SelectItem>
                  <SelectItem value="closed">Fermé</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.priority || 'all'}
                onValueChange={(value) => setFilters({ ...filters, priority: value as FeedbackPriority | 'all' })}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Priorité" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">Toutes priorités</SelectItem>
                  <SelectItem value="high">Haute</SelectItem>
                  <SelectItem value="medium">Moyenne</SelectItem>
                  <SelectItem value="low">Faible</SelectItem>
                </SelectContent>
              </Select>
              <Select
                value={filters.period || '30d'}
                onValueChange={(value) => setFilters({ ...filters, period: value as '7d' | '30d' | '90d' | 'all' })}
              >
                <SelectTrigger className="w-[140px]">
                  <SelectValue placeholder="Période" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="7d">7 derniers jours</SelectItem>
                  <SelectItem value="30d">30 derniers jours</SelectItem>
                  <SelectItem value="90d">90 derniers jours</SelectItem>
                  <SelectItem value="all">Tout</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Table */}
        <Card>
          <CardContent className="p-0">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="w-[100px]">Type</TableHead>
                  <TableHead>Sujet</TableHead>
                  <TableHead>Utilisateur</TableHead>
                  <TableHead>Établissement</TableHead>
                  <TableHead className="w-[100px]">Priorité</TableHead>
                  <TableHead className="w-[100px]">Statut</TableHead>
                  <TableHead className="w-[120px]">Date</TableHead>
                  <TableHead className="w-[80px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {feedbacks.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={8} className="text-center py-8 text-muted-foreground">
                      Aucun feedback trouvé
                    </TableCell>
                  </TableRow>
                ) : (
                  feedbacks.map((feedback) => {
                    const TypeIcon = typeIcons[feedback.type];
                    return (
                      <TableRow
                        key={feedback.id}
                        className="cursor-pointer hover:bg-muted/50"
                        onClick={() => handleOpenDetail(feedback)}
                      >
                        <TableCell>
                          <Badge variant="outline" className={cn("gap-1", getTypeColor(feedback.type))}>
                            <TypeIcon className="h-3 w-3" />
                            {getTypeLabel(feedback.type)}
                          </Badge>
                        </TableCell>
                        <TableCell className="font-medium max-w-[200px] truncate">
                          {feedback.subject}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {feedback.user_email}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {feedback.establishment_name || '-'}
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getPriorityColor(feedback.priority)}>
                            {getPriorityLabel(feedback.priority)}
                          </Badge>
                        </TableCell>
                        <TableCell>
                          <Badge variant="outline" className={getStatusColor(feedback.status)}>
                            {getStatusLabel(feedback.status)}
                          </Badge>
                        </TableCell>
                        <TableCell className="text-muted-foreground text-sm">
                          {format(new Date(feedback.created_at), 'dd MMM yyyy', { locale: fr })}
                        </TableCell>
                        <TableCell>
                          <Button
                            variant="ghost"
                            size="sm"
                            onClick={(e) => {
                              e.stopPropagation();
                              setDeleteConfirmId(feedback.id);
                            }}
                          >
                            <Trash2 className="h-4 w-4 text-destructive" />
                          </Button>
                        </TableCell>
                      </TableRow>
                    );
                  })
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        {/* Detail Dialog */}
        <Dialog open={!!selectedFeedback} onOpenChange={() => setSelectedFeedback(null)}>
          <DialogContent className="max-w-2xl max-h-[90vh] overflow-hidden flex flex-col">
            <DialogHeader>
              <DialogTitle className="flex items-center gap-2">
                {selectedFeedback && (
                  <>
                    {(() => {
                      const TypeIcon = typeIcons[selectedFeedback.type];
                      return <TypeIcon className="h-5 w-5" />;
                    })()}
                    {selectedFeedback.subject}
                  </>
                )}
              </DialogTitle>
              <DialogDescription>
                Détails du feedback
              </DialogDescription>
            </DialogHeader>

            {selectedFeedback && (
              <ScrollArea className="flex-1 pr-4">
                <div className="space-y-6">
                  {/* Status and Priority controls */}
                  <div className="flex flex-wrap gap-4">
                    <div className="space-y-1.5">
                      <Label>Statut</Label>
                      <Select
                        value={selectedFeedback.status}
                        onValueChange={(v) => handleStatusChange(v as FeedbackStatus)}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="new">Nouveau</SelectItem>
                          <SelectItem value="in_progress">En cours</SelectItem>
                          <SelectItem value="resolved">Résolu</SelectItem>
                          <SelectItem value="closed">Fermé</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                    <div className="space-y-1.5">
                      <Label>Priorité</Label>
                      <Select
                        value={selectedFeedback.priority}
                        onValueChange={(v) => handlePriorityChange(v as FeedbackPriority)}
                      >
                        <SelectTrigger className="w-[140px]">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="high">Haute</SelectItem>
                          <SelectItem value="medium">Moyenne</SelectItem>
                          <SelectItem value="low">Faible</SelectItem>
                        </SelectContent>
                      </Select>
                    </div>
                  </div>

                  {/* Message */}
                  <div className="space-y-1.5">
                    <Label>Message</Label>
                    <div className="p-3 bg-muted rounded-lg text-sm whitespace-pre-wrap">
                      {selectedFeedback.message}
                    </div>
                  </div>

                  {/* Screenshot */}
                  {selectedFeedback.screenshot_path && (
                    <div className="space-y-1.5">
                      <Label className="flex items-center gap-2">
                        <ImageIcon className="h-4 w-4" />
                        Capture d'écran
                      </Label>
                      <a
                        href={getScreenshotUrl(selectedFeedback.screenshot_path)}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="block"
                      >
                        <img
                          src={getScreenshotUrl(selectedFeedback.screenshot_path)}
                          alt="Screenshot"
                          className="max-h-48 rounded-lg border object-contain"
                        />
                      </a>
                    </div>
                  )}

                  {/* User info */}
                  <div className="space-y-2">
                    <Label>Informations utilisateur</Label>
                    <div className="grid grid-cols-2 gap-2 text-sm">
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Mail className="h-4 w-4" />
                        <a href={`mailto:${selectedFeedback.user_email}`} className="hover:underline">
                          {selectedFeedback.user_email}
                        </a>
                      </div>
                      {selectedFeedback.establishment_name && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Building2 className="h-4 w-4" />
                          {selectedFeedback.establishment_name}
                        </div>
                      )}
                      <div className="flex items-center gap-2 text-muted-foreground">
                        <Calendar className="h-4 w-4" />
                        {format(new Date(selectedFeedback.created_at), "dd MMMM yyyy 'à' HH:mm", { locale: fr })}
                      </div>
                      {selectedFeedback.page_url && (
                        <div className="flex items-center gap-2 text-muted-foreground">
                          <Globe className="h-4 w-4" />
                          <a
                            href={selectedFeedback.page_url}
                            target="_blank"
                            rel="noopener noreferrer"
                            className="hover:underline truncate max-w-[200px]"
                          >
                            {selectedFeedback.page_url}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>

                  {/* Browser info */}
                  {selectedFeedback.browser_info && (
                    <div className="space-y-1.5">
                      <Label className="flex items-center gap-2">
                        <Monitor className="h-4 w-4" />
                        Informations techniques
                      </Label>
                      <div className="p-3 bg-muted rounded-lg text-xs font-mono text-muted-foreground">
                        <p>Écran: {(selectedFeedback.browser_info as any).windowWidth}x{(selectedFeedback.browser_info as any).windowHeight}</p>
                        <p className="truncate">UA: {(selectedFeedback.browser_info as any).userAgent}</p>
                      </div>
                    </div>
                  )}

                  {/* Admin notes */}
                  <div className="space-y-1.5">
                    <Label>Notes admin</Label>
                    <Textarea
                      value={adminNotes}
                      onChange={(e) => setAdminNotes(e.target.value)}
                      placeholder="Ajoutez des notes internes..."
                      className="min-h-[80px]"
                    />
                  </div>
                </div>
              </ScrollArea>
            )}

            <DialogFooter className="mt-4">
              <Button
                variant="outline"
                onClick={() => window.open(`mailto:${selectedFeedback?.user_email}?subject=Re: ${selectedFeedback?.subject}`, '_blank')}
              >
                <Mail className="h-4 w-4 mr-2" />
                Répondre par email
              </Button>
              <Button onClick={handleSaveNotes}>
                Sauvegarder les notes
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>

        {/* Delete confirmation */}
        <Dialog open={!!deleteConfirmId} onOpenChange={() => setDeleteConfirmId(null)}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Supprimer ce feedback ?</DialogTitle>
              <DialogDescription>
                Cette action est irréversible.
              </DialogDescription>
            </DialogHeader>
            <DialogFooter>
              <Button variant="outline" onClick={() => setDeleteConfirmId(null)}>
                Annuler
              </Button>
              <Button
                variant="destructive"
                onClick={() => {
                  if (deleteConfirmId) {
                    deleteFeedback(deleteConfirmId);
                    setDeleteConfirmId(null);
                  }
                }}
              >
                Supprimer
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </div>
    </div>
  );
}
